import { useContext, useMemo } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { useParams } from '@tanstack/react-router';
import { T, Var, useGT } from 'gt-react';
import type { PricelistCaseResponse } from '@erp/rpc';
import { AuthContext } from '@/context/auth.context';
import { useUsersInOrg } from '@/api/rpc/hooks/core/useUsersInOrg';
import { orpc } from '@/api/rpc/client';
import Page from 'src/components/page/Page';
import { Badge } from '@/components/ui/badge';
import { formatFollowUpTime } from '@/components/common/waiting-on/followUpFormat';
import { pricelistVendorName } from '@/components/company/companyPickerDisplay';
import {
  caseVendorsFromLegacy,
  formatPricelistCaseVendorNames,
} from './components/pricelistCaseVendors';
import { formatDateOnlyString } from '@/utils/dateUtils';
import { formatFilesReadyDate } from './components/pricelistCaseFilesReady';
import { Field, SectionLabel } from './components/pricelistDetailFields';
import { PricelistDetailsCardBody } from './components/PricelistDetailsCard';
import { usePricelistCaseDetailsForm } from './components/usePricelistDetailsForm';
import { PricelistCaseActionsSidebar } from './components/PricelistCaseActionsSidebar';
import { PricelistCaseFilesPanel } from './components/PricelistCaseFilesPanel';
import { PricelistCaseLogFieldsCard } from './components/PricelistCaseLogFieldsCard';
import { CompanyTags } from './components/CompanyTags';
import { PricelistCaseTimeline } from './components/PricelistCaseTimeline';
import { PricelistCaseComments } from './components/PricelistCaseComments';
import { usePricelistCaseWaitingOnLabel } from './components/pricelistCaseLabels';
import { isPricelistCaseOpen } from './components/pricelistCaseOpen';
import { usePricelistCaseAttachDrop } from './components/usePricelistCaseAttachDrop';
import { clearDeletedPricelistCaseFile } from './components/pricelistCaseFileQueries';
import { firstCsvDocumentId } from './components/pricelistCaseSelectedFile';
import { useSelectedPricelistCaseFile } from './components/useSelectedPricelistCaseFile';
import { PricelistUploadModal } from './PricelistUploadModal';

const ROUTE_ID = '/authenticated/item-master/pricelist-cases/$caseId';

/**
 * One pricelist update case — the front door for a vendor price change.
 *
 * The design is explicit that no task page is pinned for this type: the case
 * page carries the domain (the metadata card, reading the CASE's values), the
 * work (the companion task's status, owner and history) and the conversation
 * (comments against that task, authorized as the case). Two front doors on day
 * one is a support problem.
 */

/**
 * The follow-up date, with its time-of-day only when one was actually chosen —
 * `formatFollowUpTime` returns null at midnight, which is what a date-only
 * pick stores, so "12:00 AM" never reads as a deliberate time.
 */
function formatFollowUpDate(followUpAt: Date | null): string {
  if (followUpAt === null) return '—';
  const date = new Date(followUpAt);
  const day = date.toLocaleDateString(undefined, { dateStyle: 'medium' });
  const time = formatFollowUpTime(date);
  return time ? `${day} · ${time}` : day;
}

/** The case's own facts, above the editable domain fields. */
function CaseContextFields({
  pricelistCase,
}: {
  pricelistCase: PricelistCaseResponse;
}) {
  const gt = useGT();
  const waitingOnLabel = usePricelistCaseWaitingOnLabel();
  const followUpText = formatFollowUpDate(pricelistCase.followUpAt);
  return (
    <div className="flex flex-wrap gap-x-10 gap-y-3">
      <Field label={<T context="pricelist case field label">Vendor</T>}>
        <span className="flex flex-wrap items-center gap-2">
          {formatPricelistCaseVendorNames(
            caseVendorsFromLegacy(
              pricelistCase.companyId,
              pricelistCase.companyName,
              pricelistCase.vendors,
            ),
            pricelistVendorName,
          )}
          {/* Beside the vendor, because that's whose tag it is — the same
              tag the companies list and the file page show. */}
          <CompanyTags
            shipAndDebitEnabled={pricelistCase.shipAndDebitEnabled}
          />
        </span>
      </Field>
      <Field label={<T context="pricelist case field label">Files</T>}>
        {String(pricelistCase.fileCount)}
      </Field>
      <Field label={<T context="pricelist case field label">Opened</T>}>
        {new Date(pricelistCase.createdAt).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}
      </Field>
      <Field label={<T context="pricelist case field label">Last change</T>}>
        {new Date(pricelistCase.updatedAt).toLocaleString(undefined, {
          dateStyle: 'medium',
          timeStyle: 'short',
        })}
      </Field>
      <Field
        label={
          <T context="pricelist case field label for what the case is parked on">
            Waiting on
          </T>
        }
      >
        {/* Resolved live from the org's option list: a disabled reason still
            renders its label, and a key no longer in the config degrades to
            the key itself rather than a blank. */}
        {waitingOnLabel(pricelistCase.waitingOnKey) ??
          gt('Not parked', {
            context: 'pricelist case waiting-on field when the case is active',
          })}
      </Field>
      <Field label={<T context="pricelist case field label">Follow-up date</T>}>
        {followUpText}
      </Field>
      <Field
        label={
          <T context="pricelist case field label for when the vendor file is available">
            Files ready
          </T>
        }
      >
        {formatFilesReadyDate(pricelistCase.filesReadyAt)}
      </Field>
    </div>
  );
}

/** The work half of the pairing: owner and effective date. */
function CaseTaskSummary({
  pricelistCase,
}: {
  pricelistCase: PricelistCaseResponse;
}) {
  const { task } = pricelistCase;
  return (
    <div className="flex flex-wrap items-center gap-3">
      {pricelistCase.details.effectiveDate !== null && (
        <span className="text-sm text-muted-foreground">
          <T context="pricelist case header, when the new prices take effect">
            Effective{' '}
            <Var>
              {formatDateOnlyString(
                pricelistCase.details.effectiveDate ?? undefined,
                '—',
              )}
            </Var>
          </T>
        </span>
      )}
      {task.taskDeleted && (
        <Badge variant="destructive">
          <T context="badge when a pricelist case's companion task was deleted">
            Task deleted
          </T>
        </Badge>
      )}
    </div>
  );
}

/** The metadata card, bound to the CASE rather than to a file. */
function CaseDetailsCard({
  pricelistCase,
}: {
  pricelistCase: PricelistCaseResponse;
}) {
  const gt = useGT();
  const form = usePricelistCaseDetailsForm(
    pricelistCase.id,
    pricelistCase.details,
  );
  return (
    <PricelistDetailsCardBody
      form={form}
      contextFields={<CaseContextFields pricelistCase={pricelistCase} />}
      notesPlaceholder={gt('Add notes about this price change…', {
        context: 'placeholder for the notes field on a pricelist update case',
      })}
    />
  );
}

export function PricelistCaseDetailPage() {
  const { caseId } = useParams({ from: ROUTE_ID });
  const { currentUser } = useContext(AuthContext);
  const { data: users } = useUsersInOrg();
  const queryClient = useQueryClient();

  const caseQuery = useQuery(
    orpc.inventoryManagement.pricelistCases.get.queryOptions({
      input: { caseId },
    }),
  );
  const pricelistCase = caseQuery.data;
  const selected = useSelectedPricelistCaseFile(pricelistCase?.files ?? []);

  const userNameById = useMemo(
    () => new Map((users ?? []).map((user) => [user.id, user.name])),
    [users],
  );

  const canAttach =
    pricelistCase !== undefined && isPricelistCaseOpen(pricelistCase.task);
  const attach = usePricelistCaseAttachDrop({ disabled: !canAttach });

  return (
    <div
      className="relative flex h-full min-h-0 overflow-hidden"
      {...attach.dropTargetProps}
    >
      {attach.isDragActive && (
        <div className="pointer-events-none absolute inset-0 z-50 bg-background/60 p-3">
          <div className="flex h-full w-full items-center justify-center rounded-lg border-2 border-dashed border-primary bg-primary/5">
            <p className="rounded-md bg-card px-4 py-2 text-lg font-semibold text-foreground shadow-sm">
              <T context="overlay label while dragging files over a pricelist case, dropping attaches them">
                Drop files to attach to this price change
              </T>
            </p>
          </div>
        </div>
      )}
      <div className="min-w-0 flex-1 overflow-y-auto">
        <Page
          title={pricelistCase?.caseNumber ?? undefined}
          isLoading={caseQuery.isPending}
          error={caseQuery.error ?? undefined}
        >
          {pricelistCase ? (
            <div className="flex flex-col gap-2">
              <CaseTaskSummary pricelistCase={pricelistCase} />
              <CaseDetailsCard pricelistCase={pricelistCase} />
              <PricelistCaseLogFieldsCard pricelistCase={pricelistCase} />
              <PricelistCaseFilesPanel
                pricelistCase={pricelistCase}
                selectedFile={selected.selectedFile}
                onSelectedDocumentIdChange={selected.setSelectedDocumentId}
                csvDocumentId={firstCsvDocumentId(selected.selectedPricelist)}
                isPricelistPending={selected.isPricelistPending}
                onUploadClick={attach.openEmptyUpload}
                userNameById={userNameById}
              />
              <div className="grid gap-2 lg:grid-cols-[2fr_1fr]">
                <PricelistCaseTimeline
                  pricelistCase={pricelistCase}
                  userNameById={userNameById}
                />
                <div className="flex flex-col gap-2">
                  <SectionLabel>
                    <T context="heading above the notes on a pricelist update case">
                      Discussion
                    </T>
                  </SectionLabel>
                  <PricelistCaseComments
                    caseId={pricelistCase.id}
                    currentUserId={currentUser?.id}
                    userNameById={userNameById}
                    className="max-h-96"
                  />
                </div>
              </div>
            </div>
          ) : (
            <div className="py-6 text-sm text-muted-foreground">
              <T context="empty state, pricelist update case not found">
                We couldn't find that price change.
              </T>
            </div>
          )}
        </Page>
      </div>

      {pricelistCase ? (
        <>
          <PricelistCaseActionsSidebar
            pricelistCase={pricelistCase}
            selectedPricelist={selected.selectedPricelist}
            onFileDeleted={() =>
              clearDeletedPricelistCaseFile(
                queryClient,
                selected.selectedPricelist?.id,
              )
            }
          />
          <PricelistUploadModal
            open={attach.uploadOpen}
            onOpenChange={attach.onUploadOpenChange}
            initialFile={attach.pendingFile}
            initialFiles={attach.pendingFiles}
            caseContext={{
              caseId: pricelistCase.id,
              caseNumber: pricelistCase.caseNumber,
              companyName: formatPricelistCaseVendorNames(
                caseVendorsFromLegacy(
                  pricelistCase.companyId,
                  pricelistCase.companyName,
                  pricelistCase.vendors,
                ),
                pricelistVendorName,
              ),
            }}
          />
        </>
      ) : undefined}
    </div>
  );
}
